import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ViewChild,
} from '@angular/core';
import { UserBoxData } from '@icc/shared/user-box-data';

@Component({
    selector: 'icc-user-box',
    templateUrl: './user-box.component.html',
    styleUrls: ['./user-box.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserBoxComponent {
    menuIsClosed = true;
    @Output() changedPriceVersion = new EventEmitter<number>();
    @ViewChild('priceVersionSelector') priceVersionSelector: any;
    @Output() logout = new EventEmitter<void>();

    public stylesSet = false;
    public userData: UserBoxData = {
        userId: 0,
        userName: '',
        email: '',
        avatar: '',
        activePanelName: '',
        activeCompanyName: null,
        activeCompanyId: null,
        panels: [],
        editUserDataURL: null,
        markets: [],
        activeMarketId: null,
        languages: [],
        activeLanguage: null,
        activeContentLanguage: null,
        activeScope: null,
        activePriceVersionId: null,
        activeProductBaseId: null,
        productBases: null,
        priceVersions: null,
        userBoxBasesAndPricesVisible: false,
        productBasesVisible: false,
        isLicenced: false,
        actualPriceVersionId: null,
        envDbVersion: null,
        hostname: window.location.origin,
    };
    avatarError = false;

    @Input()
    set data(_data: UserBoxData | string) {
        if (typeof _data === 'string') {
            this.userData = JSON.parse(_data);
        } else {
            this.userData = _data;
        }
        if (window['userBox'] && window['userBox'].condensedUserMenu !== undefined) {
            this.condensedUserMenu = window['userBox'].condensedUserMenu === true;
        }

        this.handleAvatar();
    }

    @Input() disableActions = false;

    @Input() condensedUserMenu = false;

    maxLength = (value: string, length = 28): string =>
        value.length > length ? value.substring(0, length - 3) + '...' : value;

    public onMenuToggle(isClosed: boolean) {
        this.menuIsClosed = isClosed;
        if (!isClosed && !this.stylesSet) {
            if (navigator?.platform?.toLowerCase().includes('win')) {
                this.stylesSet = true;
            } else {
                this.stylesSet = false;
            }
        }
    }

    public onOptionsSelected(panel: string | number, companyId: string | number) {
        const data = new FormData();
        data.append('companyId', companyId.toString());
        const language = this.userData.activeContentLanguage || this.userData.activeLanguage;
        fetch(`/iccadmin/${language ? (language + '/') : '' }${panel}/panel`, {
            method: 'POST',
            cache: 'no-cache',
            redirect: 'follow',
            body: data,
        }).then((response) => {
            window.location.href = response.url;
        });
    }

    public onMarketSelected(marketId: number) {
        const data = new FormData();
        data.append('marketId', marketId.toString());

        fetch('/iccadmin/default/panel', {
            method: 'POST',
            cache: 'no-cache',
            redirect: 'follow',
            body: data,
        }).then((response) => {
            window.location.href = response.url;
        });
    }

    public onLanguageSelected(langId: string) {
        const data = new FormData();
        data.append('languageId', langId);

        fetch('/iccadmin/' + this.userData.activeScope + '/panel', {
            method: 'POST',
            cache: 'no-cache',
            redirect: 'follow',
            body: data,
        }).then((response) => {
            window.location.href = response.url;
        });
    }

    public onBaseSelected(baseId: number) {
        const scope = this.userData.activeScope;
        const companyId = this.userData.activeCompanyId;
        if (
            baseId &&
            companyId &&
            companyId !== 'noContext' &&
            ['icadmin', 'company'].includes(scope)
        ) {
            fetch(
                '/iccadmin/pl/icadmin/product-base/product-base/activate-product-base/' + baseId,
                {
                    method: 'GET',
                    cache: 'no-cache',
                }
            ).then((response) => {
                window.location.href = response.url;
            });
        }
    }

    public onPriceVersionSelected(priceVersionId: number) {
        const scope = this.userData.activeScope;
        const companyId = this.userData.activeCompanyId;
        if (
            priceVersionId &&
            companyId &&
            companyId !== 'noContext' &&
            ['icadmin', 'company', 'trade'].includes(scope)
        ) {
            this.changedPriceVersion.emit(priceVersionId);
            fetch('/api/b2b/price-versions/activate/' + priceVersionId, {
                method: 'GET',
                cache: 'no-cache',
            }).then((response) => {
                window.location.reload();
            });
        }
    }

    public onUserSetup() {
        const data = new FormData();
        data.append('companyId', 'noContext');
        fetch('/iccadmin/useraccount/panel', {
            method: 'POST',
            cache: 'no-cache',
            redirect: 'follow',
            body: data,
        }).then(() => {
            window.location.href = this.userData.editUserDataURL;
        });
    }

    private handleAvatar() {
        if (!this.userData.avatar) {
            this.userData.avatar = '/img/no-avatar.png';
        }
    }

    public handleMissingAvatar() {
        this.avatarError = true;
    }

    public onClickStopPropagation($event) {
        $event.stopPropagation();
        $event.preventDefault();
    }

    public isLanguageSelectVisible(): boolean {
        return this.userData.languages?.length > 1;
    }

    public isMarketSelectVisible(): boolean {
        return this.userData.markets.length > 1 && this.userData.activeScope === 'trade';
    }

    public isBasesVisible(): boolean {
        return (
            this.userData.activeProductBaseId &&
            this.userData.productBases &&
            Object.values(this.userData.productBases).length > 1 &&
            this.userData.userBoxBasesAndPricesVisible &&
            this.userData.isLicenced &&
            ['icadmin', 'company', 'trade'].includes(this.userData.activeScope) &&
            this.userData.productBasesVisible
        );
    }

    public isPriceVersionsVisible(): boolean {
        return (
            this.userData.activePriceVersionId &&
            this.userData.priceVersions &&
            this.userData.priceVersions?.length > 1 &&
            this.userData.isLicenced &&
            ['icadmin', 'company', 'trade'].includes(this.userData.activeScope)
        );
    }

    public isCompanyNameVisible() {
        return (
            this.userData.activeCompanyName &&
            this.userData.activeCompanyId  &&
            this.userData.activeCompanyId !== 'noContext' &&
            ['icadmin', 'company', 'trade', 'driver'].includes(this.userData.activeScope)
        );
    }

    public panelIsActive(company: UserBoxData['panels'][0]) {
        if (
            company.companyId === this.userData.activeCompanyId &&
            this.userData.activeCompanyId &&
            this.userData.activeCompanyId !== 'noContext' &&
            ['icadmin', 'company', 'trade', 'driver'].includes(this.userData.activeScope)
        ) {
            return true;
        } else if (
            (
                !this.userData.activeCompanyId ||
                this.userData.activeCompanyId === 'noContext' ||
                ['setup', 'superadmin'].includes(this.userData.activeScope)
            ) && (company.companyId === 'noContext' || !company.companyId)
        ) {
            return true;
        }
        return false;
    }

    mobileLogout() {
        this.logout.emit();
    }
}
